import ChevronRight from "lucide-react/dist/esm/icons/chevron-right.mjs";
import Clock from "lucide-react/dist/esm/icons/clock.mjs";
import Headphones from "lucide-react/dist/esm/icons/headphones.mjs";
import BookmarkPlus from "lucide-react/dist/esm/icons/bookmark-plus.mjs";
import Search from "lucide-react/dist/esm/icons/search.mjs";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check.mjs";
import type { ArtworkRef, BriefingSummary, DailyEntry, InboxBrief, LibrarySection, PodcastEpisode, ReaderItem } from "../types";
import { ArtworkImage } from "./ArtworkImage";

export function ReadingList({
  title,
  subtitle,
  section,
  items,
  briefs = [],
  podcastEpisodes = [],
  briefings = [],
  selectedId,
  entries,
  coverArtwork,
  loading,
  error,
  incompleteNotice,
  retrySearch = false,
  offline,
  hasMore,
  onOpen,
  onOpenBrief,
  onOpenPodcastEpisode,
  onOpenBriefing,
  onLoadMore,
  onRetry,
  searchQuery = "",
  libraryControls,
}: {
  title: string;
  subtitle: string;
  section: LibrarySection | "search";
  items: ReaderItem[];
  briefs?: InboxBrief[];
  podcastEpisodes?: PodcastEpisode[];
  briefings?: BriefingSummary[];
  selectedId?: string;
  entries?: DailyEntry[];
  coverArtwork?: ArtworkRef | null;
  loading: boolean;
  error?: string;
  incompleteNotice?: string;
  retrySearch?: boolean;
  offline: boolean;
  hasMore: boolean;
  onOpen: (item: ReaderItem) => void;
  onOpenBrief?: (brief: InboxBrief) => void;
  onOpenPodcastEpisode?: (episode: PodcastEpisode) => void;
  onOpenBriefing?: (briefing: BriefingSummary) => void;
  onLoadMore: () => void;
  onRetry?: () => void;
  searchQuery?: string;
  libraryControls?: {
    scope: "library" | "starred" | "archive";
    online: boolean;
    onScope: (scope: "library" | "starred" | "archive") => void;
    onSave: () => void;
  };
}) {
  const entryMap = new Map((entries || []).map((entry) => [entry.item_id, entry]));
  const count = items.length + briefs.length + podcastEpisodes.length + briefings.length;
  const empty = count === 0;
  const emptyCopy = emptyStateCopy(section, searchQuery);
  const state = error
    ? "error"
    : loading && empty
      ? "loading"
      : incompleteNotice
        ? "incomplete"
        : empty
          ? "empty"
          : null;
  return (
    <section className={`reading-list${section === "daily" ? " reading-list--daily" : ""}`} aria-labelledby="reading-list-title">
      <header className="reading-list__header">
        {section === "daily" && coverArtwork ? <ArtworkImage artwork={coverArtwork} className="daily-cover-artwork" loading="eager" /> : null}
        <div className="reading-list__heading">
          <div>
            <h1 id="reading-list-title">{title}</h1>
            <p
              aria-live={section === "search" ? "polite" : undefined}
              aria-atomic={section === "search" ? "true" : undefined}
              aria-busy={section === "search" ? loading : undefined}
            >
              {subtitle}
            </p>
          </div>
          {libraryControls ? (
            <button className="library-save-command" type="button" disabled={!libraryControls.online} onClick={libraryControls.onSave}>
              <BookmarkPlus aria-hidden="true" />
              <span>Save a page</span>
            </button>
          ) : null}
        </div>
        {libraryControls ? (
          <nav className="library-scopes" aria-label="Library scopes">
            {(["library", "starred", "archive"] as const).map((scope) => (
              <button key={scope} type="button" className={scope === libraryControls.scope ? "active" : undefined} aria-current={scope === libraryControls.scope ? "page" : undefined} onClick={() => libraryControls.onScope(scope)}>
                {scope === "library" ? "All" : scope === "starred" ? "Starred" : "Archive"}
              </button>
            ))}
          </nav>
        ) : null}
      </header>
      <div className="reading-list__rows" aria-busy={loading && state !== "error"}>
        {empty ? null : (
          <div className="reading-list__results" role="list" aria-labelledby="reading-list-title">
            {briefings.map((briefing) => <BriefingRow key={briefing.id} briefing={briefing} selected={briefing.id === selectedId} onOpen={onOpenBriefing} />)}
            {podcastEpisodes.map((episode) => <PodcastEpisodeRow key={episode.id} episode={episode} selected={episode.id === selectedId} onOpen={onOpenPodcastEpisode} />)}
            {briefs.map((brief) => <InboxBriefRow key={brief.id} brief={brief} selected={brief.id === selectedId} onOpen={onOpenBrief} />)}
            {items.map((item) => (
              <ReadingRow key={item.id} item={item} selected={item.id === selectedId} dailyEntry={entryMap.get(item.id)} onOpen={onOpen} />
            ))}
          </div>
        )}
        {state === "empty" ? (
          <div className="empty-reading-list">
            <Search aria-hidden="true" />
            <h2>{emptyCopy.title}</h2>
            <p>{emptyCopy.body}</p>
          </div>
        ) : null}
        {state === "loading" ? <ListLoading searching={section === "search"} /> : null}
        {state === "incomplete" ? <p className="margin-note" role="status">{incompleteNotice}</p> : null}
        {state === "incomplete" && retrySearch && onRetry ? <button className="load-more" type="button" disabled={loading} onClick={onRetry}>Retry search</button> : null}
        {state === "error" ? <div className="margin-note inline-recovery" role="alert"><p>{error}</p>{onRetry ? <button type="button" disabled={section === "search" && loading} onClick={onRetry}>Retry</button> : null}</div> : null}
        {hasMore && state !== "error" ? <button className="load-more" type="button" disabled={loading} onClick={onLoadMore}>{section === "search" ? loading ? "Searching…" : "Continue search" : "Load more"}</button> : null}
      </div>
      <footer className="reading-list__footer">
        <span>{state === "loading" ? "Opening…" : `${count} read${count === 1 ? "" : "s"}`}</span>
        {offline ? <span>Offline copies</span> : null}
      </footer>
    </section>
  );
}

function ListLoading({ searching = false }: { searching?: boolean }) {
  return (
    <div className="list-loading" role="status">
      <span className="sr-only">{searching ? "Searching saved reads…" : "Opening saved reads…"}</span>
      {[0, 1, 2].map((row) => (
        <div className="list-loading__row" aria-hidden="true" key={row}>
          <span className="loading-line loading-line--title" />
          <span className="loading-line loading-line--meta" />
          <span className="loading-line loading-line--short" />
        </div>
      ))}
    </div>
  );
}

function emptyStateCopy(section: LibrarySection | "search", searchQuery: string): { title: string; body: string } {
  if (section === "search" && !searchQuery.trim()) return { title: "Find a saved read", body: "Search titles, authors, sites, tags, or a phrase from an article." };
  if (section === "search") return { title: "No matches", body: "Try a title, author, site, tag, or phrase from an article." };
  if (section === "briefings") return { title: "No Abridged edition yet", body: "A new edition will appear when Imprint finds at least one item it can faithfully summarize." };
  if (section === "daily") return { title: "Daily is being prepared", body: "This reading issue will appear as soon as it is ready." };
  if (section === "starred") return { title: "No favorites yet", body: "Favorite a reading to keep it close here." };
  if (section === "archive") return { title: "Archive is empty", body: "Readings you archive will remain available here." };
  return { title: "Nothing saved yet", body: "Saved pages will appear here when they are ready." };
}

function BriefingRow({ briefing, selected, onOpen }: { briefing: BriefingSummary; selected: boolean; onOpen?: (briefing: BriefingSummary) => void }) {
  return (
    <div role="listitem">
      <button data-reading-list-item-id={briefing.id} aria-current={selected ? "true" : undefined} className={`reading-row reading-row--brief${briefing.artwork_state === "ready" && briefing.artwork ? " reading-row--with-artwork" : ""}${selected ? " reading-row--selected" : ""}`} type="button" onClick={() => onOpen?.(briefing)}>
        {briefing.artwork_state === "ready" && briefing.artwork ? <ArtworkImage artwork={briefing.artwork} className="reading-row__artwork" /> : null}
        <span className="reading-row__body">
          <span className="reading-row__title"><Headphones aria-hidden="true" />{briefing.title}</span>
          <span className="reading-row__byline">{briefing.format_version === "abridged_v1" ? "Imprint Abridged" : "Earlier Briefing"} · {briefing.issue_date} · {briefing.entry_count} item{briefing.entry_count === 1 ? "" : "s"}</span>
          <span className="reading-row__brief">{briefing.subtitle}</span>
        </span>
        <span className="reading-row__signal"><ChevronRight className="reading-row__chevron" aria-hidden="true" /></span>
      </button>
    </div>
  );
}

function PodcastEpisodeRow({ episode, selected, onOpen }: { episode: PodcastEpisode; selected: boolean; onOpen?: (episode: PodcastEpisode) => void }) {
  const voices = episode.variants.map((variant) => variant.voice.charAt(0).toUpperCase() + variant.voice.slice(1)).join(" + ");
  return (
    <div role="listitem">
      <button data-reading-list-item-id={episode.id} aria-current={selected ? "true" : undefined} className={`reading-row reading-row--brief reading-row--podcast${episode.artwork_state === "ready" && episode.artwork ? " reading-row--with-artwork" : ""}${selected ? " reading-row--selected" : ""}`} type="button" onClick={() => onOpen?.(episode)}>
        {episode.artwork_state === "ready" && episode.artwork ? <ArtworkImage artwork={episode.artwork} className="reading-row__artwork" /> : null}
        <span className="reading-row__body">
          <span className="reading-row__title"><Headphones aria-hidden="true" />{episode.title}</span>
          <span className="reading-row__byline">Earlier Briefing · {voices || "Cedar"}</span>
          <span className="reading-row__brief">{episode.subtitle}</span>
        </span>
        <span className="reading-row__signal"><ChevronRight className="reading-row__chevron" aria-hidden="true" /></span>
      </button>
    </div>
  );
}

function InboxBriefRow({ brief, selected, onOpen }: { brief: InboxBrief; selected: boolean; onOpen?: (brief: InboxBrief) => void }) {
  return (
    <div role="listitem">
      <button data-reading-list-item-id={brief.id} aria-current={selected ? "true" : undefined} className={`reading-row reading-row--brief${selected ? " reading-row--selected" : ""}`} type="button" onClick={() => onOpen?.(brief)}>
        <span className="reading-row__body">
          <span className="reading-row__title"><ShieldCheck aria-hidden="true" />{brief.title}</span>
          <span className="reading-row__byline">Inbox Brief · {brief.covered_count} source{brief.covered_count === 1 ? "" : "s"}</span>
          <span className="reading-row__brief">{brief.subtitle}</span>
        </span>
        <span className="reading-row__signal"><ChevronRight className="reading-row__chevron" aria-hidden="true" /></span>
      </button>
    </div>
  );
}

function ReadingRow({ item, selected, dailyEntry, onOpen }: { item: ReaderItem; selected: boolean; dailyEntry?: DailyEntry; onOpen: (item: ReaderItem) => void }) {
  const author = item.byline || item.author_name || item.author?.name || "";
  const source = item.site_name || safeHost(item.url);
  const progress = Math.round((item.reading_progress || 0) * 100);
  return (
    <div role="listitem">
      <button data-reading-list-item-id={item.id} aria-current={selected ? "true" : undefined} className={`reading-row${selected ? " reading-row--selected" : ""}${item.editorial_recommendation ? " reading-row--recommended" : ""}${item.library_placement?.kind === "lift" ? " reading-row--lift" : ""}${dailyEntry && item.artwork_state === "ready" && item.artwork ? " reading-row--with-artwork" : ""}`} type="button" onClick={() => onOpen(item)}>
        {dailyEntry && item.artwork_state === "ready" && item.artwork ? <ArtworkImage artwork={item.artwork} className="reading-row__artwork" ownerItemId={item.id} /> : null}
        <span className="reading-row__body">
          {item.library_placement ? <span className="reading-row__placement">{item.library_placement.label}</span> : null}
          <span className="reading-row__title">{item.title || "Untitled read"}</span>
          <span className="reading-row__byline">{[author, source].filter(Boolean).join(" · ")}</span>
          {item.library_placement?.kind === "lift" && item.library_placement.reason ? <span className="reading-row__lift-reason">{item.library_placement.reason}</span> : null}
          {dailyEntry?.one_line_brief ? <span className="reading-row__brief">{dailyEntry.one_line_brief}</span> : null}
          <span className="reading-row__meta">
            {item.estimated_minutes ? <><Clock aria-hidden="true" />{item.estimated_minutes} min</> : null}
            {item.reader_status && item.reader_status !== "readable" ? <span>{readerStatus(item.reader_status)}</span> : null}
          </span>
        </span>
        <span className="reading-row__signal">
          {progress > 0 ? <span>{progress}%</span> : null}
          {progress > 0 ? <progress className="mini-progress" max={100} value={progress} aria-hidden="true" /> : null}
          <ChevronRight className="reading-row__chevron" aria-hidden="true" />
        </span>
      </button>
    </div>
  );
}

function safeHost(value: string): string {
  try { return new URL(value).hostname.replace(/^www\./, ""); } catch { return ""; }
}

function readerStatus(value: string): string {
  return value.replaceAll("_", " ").replace(/^./, (character) => character.toUpperCase());
}
